import type { Match, Player } from '../types';
import { matchPositionSettings } from './matchPlayerPositions';

/** Missing preferences block planning, never remove selected players from the squad. */
export function playersMissingMatchPreferences(
  players: Player[], unavailableIds: string[], preferences: (player: Player) => unknown[],
): Player[] {
  const unavailable = new Set(unavailableIds);
  return [...new Map(players.map(player => [player.id, player])).values()]
    .filter(player => !unavailable.has(player.id) && preferences(player).length === 0);
}

export const matchSquadLimit = (variant: string): number => variant === 'indoor' ? 12 : 16;
/** Keep all actual bench entries visible, including incomplete/historical plans. */
export const matchBenchRows = (variant: string, fieldPositions: number, actualCounts: number[] = []): number =>
  Math.max(0, matchSquadLimit(variant) - fieldPositions, ...actualCounts);

type MatchSelectionSnapshot = Pick<Match, 'preparation_settings'> & Partial<Pick<Match,
  'lineup' | 'reserve_players' | 'standin_players' | 'unavailable_players' | 'substitution_schedule'>>;

/** A saved plan owns its participants independently of subsequent team roster additions. */
export function plannedMatchPlayerIds(match: MatchSelectionSnapshot): Set<string> | null {
  const schedule = match.substitution_schedule ?? {};
  const scheduled = Object.entries(schedule).filter(([key]) => /^.+-\d+-\d+$/.test(key));
  if (!scheduled.length) return null;
  const refs: unknown[] = [...scheduled.map(([, ref]) => ref), ...Object.values(match.lineup ?? {}),
    ...(match.reserve_players ?? []), ...(match.standin_players ?? []), ...(match.unavailable_players ?? [])];
  return new Set(refs.map(ref => typeof ref === 'string' ? ref
    : ref && typeof ref === 'object' && 'id' in ref ? String(ref.id) : '').filter(Boolean));
}

export const notSelectedPlayerIds = (match: MatchSelectionSnapshot, players: Player[] = []): string[] => {
  const planned = plannedMatchPlayerIds(match);
  return [...new Set([...(match.preparation_settings?.not_selected_players ?? []),
    ...(planned ? players.filter(player => !planned.has(player.id)).map(player => player.id) : [])])];
};

/** Persist inferred non-selection before invalidating its supporting saved plan. */
export function retainPlannedSelection(match: Match, players: Player[], updates: Partial<Match>): Partial<Match> {
  if (!plannedMatchPlayerIds(match)) return updates;
  const excluded = updates.preparation_settings?.not_selected_players ?? notSelectedPlayerIds(match, players);
  return { ...updates,
    preparation_settings: { ...match.preparation_settings, ...updates.preparation_settings, not_selected_players: excluded },
    unavailable_players: [...new Set([...(updates.unavailable_players ?? match.unavailable_players ?? []), ...excluded])],
  };
}

/** Excluded players must not acquire reserve rounds in match statistics. */
export function playersForMatchStatistics(players: Player[], match: MatchSelectionSnapshot): Player[] {
  const excluded = new Set([...notSelectedPlayerIds(match, players), ...(match.preparation_settings?.absent_not_selected_players ?? [])]);
  return players.filter(player => !excluded.has(player.id));
}

/** Preserve a coach's non-selection while actual attendance changes. */
export function changeUnselectedAttendance(match: Match, playerId: string, players: Player[] = []): Partial<Match> | null {
  const excluded = new Set(notSelectedPlayerIds(match, players));
  const absent = new Set(match.preparation_settings?.absent_not_selected_players ?? []);
  if (excluded.has(playerId)) {
    excluded.delete(playerId);
    absent.add(playerId);
  } else if (absent.has(playerId)) {
    absent.delete(playerId);
    excluded.add(playerId);
  } else return null;
  return {
    preparation_settings: { ...match.preparation_settings,
      not_selected_players: [...excluded], absent_not_selected_players: [...absent] },
    // Both states remain outside the planning pool; only actual presence changes.
    unavailable_players: [...new Set([...(match.unavailable_players ?? []), playerId])],
  };
}

/** Legacy unavailable is the planning exclusion union; this subset is present but not selected. */
export function changeMatchSelection(match: Match, playerId: string, selected: boolean, players: Player[] = []): Partial<Match> {
  const excluded = new Set(notSelectedPlayerIds(match, players));
  const unavailable = new Set([...(match.unavailable_players ?? []), ...excluded]);
  const hasStartingSlot = Object.values(match.lineup ?? {}).some(player => player?.id === playerId);
  const hasReserveSlot = (match.reserve_players ?? []).some(player => player.id === playerId);
  if (selected) { excluded.delete(playerId); unavailable.delete(playerId); }
  else { excluded.add(playerId); unavailable.add(playerId); }
  return {
    preparation_settings: { ...match.preparation_settings, match_position_overrides: matchPositionSettings(match), not_selected_players: [...excluded],
      ...(match.preparation_settings?.absent_not_selected_players ? {
        absent_not_selected_players: match.preparation_settings.absent_not_selected_players.filter(id => id !== playerId),
      } : {}),
    },
    unavailable_players: [...unavailable],
    ...(selected && plannedMatchPlayerIds(match) && players.some(player => player.id === playerId)
      && !hasStartingSlot && !hasReserveSlot
      ? { reserve_players: [...(match.reserve_players ?? []), players.find(player => player.id === playerId)!] } : {}),
    ...(!selected && hasStartingSlot ? {
      lineup: Object.fromEntries(Object.entries(match.lineup ?? {}).map(([position, player]) =>
        [position, player?.id === playerId ? null : player])),
    } : {}),
    ...(!selected && hasReserveSlot ? {
      reserve_players: (match.reserve_players ?? []).filter(player => player.id !== playerId),
    } : {}),
  };
}

export const columnBenchPlayers = (players: Player[], playingIds: string[], unavailableIds: string[], notSelectedIds: string[] = []): Player[] => {
  const excluded = new Set([...playingIds, ...unavailableIds, ...notSelectedIds]);
  return players.filter(player => !excluded.has(player.id));
};

/** Explicit non-selection also excludes a match-only or regular stand-in. */
export function matchPlanningExclusions(match: Pick<Match, 'unavailable_players' | 'preparation_settings'>, availableExtraIds: string[]): string[] {
  const explicit = new Set([...notSelectedPlayerIds(match), ...(match.preparation_settings?.absent_not_selected_players ?? [])]);
  return [...new Set([...(match.unavailable_players ?? []).filter(id => explicit.has(id) || !availableExtraIds.includes(id)), ...explicit])];
}

/** Save attendance and occupation together so API normalization cannot undo absence. */
export function markMatchPlayerAbsent(
  match: Match, playerId: string, draftLineup = match.lineup ?? {}, players: Player[] = [],
): Partial<Match> {
  const unselected = changeUnselectedAttendance(match, playerId, players);
  if (unselected) return unselected;
  const patch = changeMatchSelection(match, playerId, false, players);
  return {
    ...patch,
    preparation_settings: {
      ...patch.preparation_settings,
      not_selected_players: notSelectedPlayerIds(match, players),
    },
    lineup: Object.fromEntries(Object.entries(draftLineup).map(([position, player]) =>
      [position, player?.id === playerId ? null : player])),
  };
}
